import React, {useEffect, useState} from "react";
import {  observer } from "mobx-react-lite";
import { makeStyles } from '@material-ui/core/styles';
import Typography from "@material-ui/core/Typography";
import SearchIcon from '@material-ui/icons/Search';
import CancelIcon from '@material-ui/icons/Close';
import InputAdornment from '@material-ui/core/InputAdornment';
import {useStores} from "../hooks/use-stores";
import Alert from "@material-ui/lab/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import {useRouteMatch, Link} from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import {joinPath, patchCDN} from "../utils/helpers";
import FormControl from "@material-ui/core/FormControl";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import ThumbMediaList from "../components/ThumbMediaList";
import IconButton from "@material-ui/core/IconButton";
import debounce from "lodash/debounce";
import services from "../services";
import MediaList from "../components/MediaList";
import Favorite from "@material-ui/icons/Favorite";
import {Box} from "@material-ui/core";
import {useTranslation} from "react-i18next";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import InfoIcon from "@material-ui/icons/InfoOutlined";
import SyncIcon from "@material-ui/icons/Sync";

const useStyles = makeStyles((theme) => ({
    root:{
        color:"white",
        width:"100%",
    },
    margin:{
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },

    colorWhite:{
        color:"white",
    },
    grid: {

        "&:after":{
            content: '""',
            flex: "auto"
        }
    },

    btnSeries:{
        display:"flex",
        flexDirection:"column",
        alignItems:"center",
        color: "white",
        textDecoration:"none"
    },


    seriesIcon: {
        borderRadius: "50%",
        width: "100%"
    },
    lineClamp:{
        margin: "5px 0",
        textAlign: "center",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        lineHeight: "1.5em",
    },

    notchedOutline:{
        borderColor: "white"
    },
    favoritesPlaceholder:{
        display:"flex",
        width:120,
        height:120,
        borderRadius: 3,
        fontStyle: "italic",
        color: "#999",
        alignItems:"center",
        justifyContent:"center",
        textAlign:"center",
    },
    whiteBanner:{
        display:"flex",
        justifyContent:"center",
        alignItems:"center",
        width:"100%",
        minHeight:30,
        backgroundColor:"white",
        color:"black",
        fontSize:16,
        fontWeight:"bold",
        padding: theme.spacing(1),
        gap: 4,

    },
    bannerContent: {
        textAlign: "center",
        [theme.breakpoints.down('sm')]: {
            display: "flex",
            flexDirection: "column",
            lineHeight: "1.2",
        },
        [theme.breakpoints.up('md')]: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacing(0.5),
        },
    },
    bannerLine1: {
        [theme.breakpoints.down('sm')]: {
            fontSize: 14,
            fontWeight: "bold",
        },
    },
    bannerLine2: {
        [theme.breakpoints.down('sm')]: {
            fontSize: 14,
            fontWeight: "normal",
        },
    },
    bannerIcons: {
        display: "flex",
        gap: theme.spacing(0.5),
        alignItems: "center",
        "& svg": {
            fontSize: 18,
            cursor: "pointer",
            "&:hover": {
                opacity: 0.7,
            },
        },
    },
    popupText:{
        whiteSpace: "pre-wrap",
    }
}));

export default observer ( function News () {
    const classes = useStyles();
    const {t} = useTranslation();

    const { authStore, commonStore, libraryStore, salesAchieverAudiosStore, allTimesGreatStore, rascalTunesStore, mediaHandlers } = useStores();
    const match = useRouteMatch();
    const {audioTracking, series, speakers, libraryLoading, recentlyPlayed, recentlyPurchased, audiosFavorites, searchString, error } = libraryStore;
    const {series: salesAchieverSeries, ppsSeries } = salesAchieverAudiosStore;
    const { allTimesSeries } = allTimesGreatStore;
    const { rascalTunesSeries } = rascalTunesStore;
    const [loading, setLoading] = React.useState(false)
    const [searchResults, setSearchResults] = React.useState(null)
    const [searchError, setSearchError] = React.useState(null)
    const fetchIdRef = React.useRef(0)
    const [ lang, setLang ] = useState(commonStore.language);


    useEffect(()=>{
        ( async ()=>{
            await Promise.all([ libraryStore.loadLibrary(lang)
                , salesAchieverAudiosStore.loadSeries(lang)
                , allTimesGreatStore.loadSeries(lang)
                , rascalTunesStore.loadSeries(lang)
            ])
        })()
    },[lang])

    const showFastAudioInfoPopup = ()=>{
        commonStore.showConfirm( <p  className={classes.popupText}>
                { audioTracking.fastStartAudioCount > 0
                ? t("starfish:home.fast_start_program_description")
                : t("starfish:home.audios_listened_description")} </p>
            , "OK"
            , audioTracking.fastStartAudioCount > 0
                ? t("starfish:home.fast_audio_program")
                : t("starfish:home.audios_listened")
            , null
            , null
            , undefined
            , { hideHeader:false })
    }

    const refreshAudioTracking = async ()=>{
        try {
            await libraryStore.loadLibrary(lang);
        } catch (error) {
            console.error('Failed to refresh audio tracking:', error);
        }
    }



    const fetchData = React.useCallback( debounce( async (searchString)=>{
        // Give this fetch an ID
        const fetchId = ++fetchIdRef.current
        setSearchError(null)
        if ( !searchString || searchString.length < 2 ){
            setLoading(false)
            setSearchResults ( null )
            return
        }
        setLoading(true)
        try {
            let result = await services.Library.search(searchString)


            if (fetchId === fetchIdRef.current) {
                if ( result && result.media ){
                    setSearchResults ( result.media )
                }
                else
                    setSearchResults ( [] )

            }
        }
        catch(e){
            if (fetchId === fetchIdRef.current) {
                setSearchError(e.message)
            }
        }
        finally{
            setLoading(false)
        }
    }, 500 ), [])

    useEffect(()=>{
        fetchData(searchString)
    },[fetchData, searchString])


    const showBanner = authStore.subscriptions.hasLTBasic || authStore.currentUser.isMember

    return <div className={classes.root}>
        <Box display={"flex"} justifyContent={"space-between"}>
            <Typography variant="h5">{t("mediaLibrary")}</Typography>
            <Select
                label="Language"
                className={classes.textField}
                value ={lang}
                onChange={e=>{setLang(e.target.value)}}>
                <MenuItem value={'en'}>English</MenuItem>
                <MenuItem value={'fr'}>Français</MenuItem>
                <MenuItem value={'es'}>Español</MenuItem>
            </Select>
        </Box>
        {error ?  <Alert severity="error">{error}</Alert> : null}
        { showBanner &&
            <Box display="flex" justifyContent="center" alignItems={"center"} className={classes.whiteBanner}>
                <Box className={classes.bannerContent}>
                    {audioTracking.fastStartAudioCount > 0 ? (
                        <>
                            <Box className={classes.bannerLine1}>
                                {t("starfish:home.fast_audio")}
                            </Box>
                            <Box className={classes.bannerLine2}>
                                {audioTracking.fastStartAudioCount || ""} of 60
                                <SyncIcon 
                                    onClick={refreshAudioTracking} 
                                    titleAccess="Refresh audio count"
                                    style={{marginLeft: 8, fontSize: 16, cursor: "pointer"}}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box className={classes.bannerLine1}>
                            {t("starfish:home.audios_listened")}: {audioTracking.audioListenedCount}
                            <SyncIcon 
                                onClick={refreshAudioTracking} 
                                titleAccess="Refresh audio count"
                                style={{marginLeft: 8, fontSize: 16, cursor: "pointer"}}
                            />
                        </Box>
                    )}
                </Box>
                <InfoIcon 
                    onClick={showFastAudioInfoPopup} 
                    titleAccess="More information"
                    style={{cursor: "pointer", fontSize: 18}}
                />
            </Box>
        }


        <FormControl fullWidth className={classes.margin} variant="outlined">
            <OutlinedInput classes={{notchedOutline:classes.notchedOutline, input:classes.colorWhite}}
                id="outlined-adornment-amount"
                placeholder={t("Search")+"..."}
                value={searchString}
                autoComplete={"off"}
                onChange={e=>{ libraryStore.setSearchString(e.target.value) }}
                endAdornment={(
                    searchString && <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={e=>{libraryStore.setSearchString("") }}
                        ><CancelIcon /></IconButton>
                    </InputAdornment>)}
                startAdornment={<InputAdornment className={classes.colorWhite} position="start"><SearchIcon/></InputAdornment>}

            />
        </FormControl>

        { (libraryLoading  || loading ) ? <CircularProgress/>
            :<>
                {searchError&& <Alert variant={"filled"} severity={"error"}>{searchError}</Alert>}
                {searchResults
                    ?<>
                        {( searchResults && searchResults.length>0 )
                            ? <MediaList items={searchResults}
                                         onAudioPlay={mediaHandlers.handleAudioPlay}
                                         onOpenBook={mediaHandlers.handleBookOpen}
                                         onVideoOpen={mediaHandlers.handleVideoOpen}/>
                            : <Alert variant={"filled"} severity={"warning"}>Nothing Found</Alert>
                        }
                     </>
                    :<> { authStore.currentUser.isMember && salesAchieverSeries &&
                    <>
                        <Typography variant="h5">{t("global:salesAchieverAudios")}</Typography>
                        <ThumbMediaList items={salesAchieverSeries}
                                        onAudioPlay={mediaHandlers.handleAudioPlay}
                                        onAudioPause={mediaHandlers.handleAudioPause}
                                        forceType={"audio"}
                        />

                    </>}
                    { authStore.currentUser.isMember && ppsSeries &&
                    <>
                        <Typography variant="h5">Power Player Series</Typography>
                        <ThumbMediaList items={ppsSeries}
                                        onAudioPlay={mediaHandlers.handleAudioPlay}
                                        onAudioPause={mediaHandlers.handleAudioPause}
                                        forceType={"audio"}
                        />

                    </>}

                        { ( recentlyPurchased  && recentlyPurchased.length>0 )&&
                        <>
                            <Typography variant="h5">{t("recentlyReleased")}</Typography>
                            <ThumbMediaList items={recentlyPurchased}
                                            onAudioPlay={mediaHandlers.handleAudioPlay}
                                            onOpenBook={mediaHandlers.handleBookOpen}
                                            onVideoOpen={mediaHandlers.handleVideoOpen}
                            />

                        </>}
                        { ( recentlyPlayed && recentlyPlayed.length>0 )&&
                        <>
                            <Typography variant="h5">{t("recentlyPlayed")} </Typography>
                            <ThumbMediaList items={recentlyPlayed}
                                            onAudioPlay={mediaHandlers.handleAudioPlay}
                                            onOpenBook={mediaHandlers.handleBookOpen}
                                            onVideoOpen={mediaHandlers.handleVideoOpen}
                            />

                        </>}

                        { ( allTimesSeries && allTimesSeries.length>0 )&&
                            <>
                                <Typography variant="h5">{t("allTimeGreats")}</Typography>
                                <ThumbMediaList items={allTimesSeries}
                                                onAudioPlay={mediaHandlers.handleAudioPlay}
                                                onOpenBook={mediaHandlers.handleBookOpen}
                                                onVideoOpen={mediaHandlers.handleVideoOpen}
                                />

                            </>}

                        { ( rascalTunesSeries && rascalTunesSeries.length>0 )&&
                            <>
                                <Typography variant="h5">Rascal Tunes</Typography>
                                <ThumbMediaList items={rascalTunesSeries}
                                                onAudioPlay={mediaHandlers.handleAudioPlay}
                                                onOpenBook={mediaHandlers.handleBookOpen}
                                                onVideoOpen={mediaHandlers.handleVideoOpen}
                                />

                            </>}


                            <>
                                <Typography variant="h5" style={{display:"flex", alignItems:"center"}}>{t("Favorites")}
                                    <Favorite style={{marginLeft:10, color:"red"}}/> </Typography>
                                { ( audiosFavorites && audiosFavorites.length>0 )
                                    ? <ThumbMediaList items={audiosFavorites}
                                                      onAudioPlay={mediaHandlers.handleAudioPlay}
                                                      onOpenBook={mediaHandlers.handleBookOpen}
                                                      onVideoOpen={mediaHandlers.handleVideoOpen}
                                    />
                                    : <Box className={classes.favoritesPlaceholder}>
                                        <Typography variant="body1">{t("noFavorite")}</Typography>
                                    </Box> }


                            </>

                        <Typography variant="h5">{t("browseByCategories")}</Typography>
                        <Grid spacing={3} container direction="row" alignItems="flex-start" justifyContent="space-between" className={classes.grid}>
                            {series.map( s=>{
                                return <Grid key={s.seriesValue} item xs={4} sm={2} md={2} lg={2}>
                                    <Link to={`${joinPath( match.url, "series", s.seriesValue )}`} className={classes.btnSeries}>
                                        <img className={classes.seriesIcon}
                                             src={`https://resources.lifeinfoapp.com/public/icon/library/${s.imageURL || s.seriesValue}.png`}/>
                                        <span className={classes.lineClamp}>{s.seriesName}</span>
                                    </Link>
                                </Grid>
                            })}
                        </Grid>
                        <Typography variant="h5">{t("browseBySpeakers")}</Typography>
                        <Grid spacing={3} container direction="row" alignItems="flex-start" justifyContent="space-between" className={classes.grid}>
                            {speakers.map( s=>{
                                return <Grid key={s.speakerID} item xs={4} sm={2} md={2} lg={2}>
                                    <Link to={`${joinPath( match.url, "speakers", s.speakerID )}`} className={classes.btnSeries}>
                                        <img className={classes.seriesIcon}
                                             src={ patchCDN( s.speakerImage )}/>
                                        <span className={classes.lineClamp}>{s.speakerName}</span>
                                    </Link>
                                </Grid>
                            })}

                        </Grid>
                    </>}


            </>}

    </div>
})